import React, { Suspense } from 'react'
import {
  Flex,
  LoadingSpinner,
  Sheet,
  Transition,
  VideoPlayer,
} from '@sendoutcards/quantum-design-ui'
import { useActions, useCallback, useMutations, useState } from 'src/hooks'
import AddSponsor from './components/AddSponsor'
import FindMySponsor from './components/FindMySponsor'
import FreeCardSuccess from './components/FreeCardSuccess'
import SelectMySponsor from './components/SelectMySponsor'
import ThankYou from './components/ThankYou'
import CreateAccount from './components/CreateAccount'
import FeaturedCardsGrid from 'src/catalog/components/FeaturedCardsGrid/FeaturedCardsGrid'

type Step =
  | { type: 'success' }
  | { type: 'create' }
  | { type: 'selectMySponsor' }
  | { type: 'addSponsor' }
  | { type: 'findMySponsor' }
  | { type: 'thankYou' }

const SendFreeCardSuccess = (props: {
  orderNumber?: string
  onClose?: () => void
}) => {
  const { orderNumber, onClose } = props
  const [step, setStep] = useState<Step>({ type: 'create' })
  const [isUpdating, setIsUpdating] = useState(false)
  const [chosenSponsor, setChosenSponsor] = useState<string>()
  const actions = useActions()
  const mutations = useMutations()

  const handleChooseSponsor = useCallback(
    async (sponsorId?: string) => {
      if (isUpdating) {
        return
      }
      try {
        setIsUpdating(true)
        const {
          updateAccount: { account },
        } = await mutations.updateAccount({
          account: sponsorId
            ? { sponsor: sponsorId }
            : { sponsorGenealogyId: '9070' },
        })
        actions.updatedAccount(account)
        const sponsor = account.sponsor
        setChosenSponsor(
          sponsorId ? `${sponsor?.firstName} ${sponsor?.lastName}` : undefined,
        )
        setStep({ type: 'thankYou' })
      } catch (error) {
        console.log(`Failed to update account with sponsor id ${sponsorId}`)
      } finally {
        setIsUpdating(false)
      }
    },
    [isUpdating, setIsUpdating, actions, setChosenSponsor, setStep, mutations],
  )
  const handleHasNoPossibleSponsors = useCallback(() => {
    setStep({ type: 'addSponsor' })
  }, [setStep])

  return (
    <Sheet
      isOpen={true}
      backgroundElement={
        step.type === 'success' || step.type === 'create' ? (
          <VideoPlayer
            height="100%"
            maxHeight="1200px"
            hasShadow={true}
            poster="https://images.unsplash.com/photo-1526614180703-827d23e7c8f2?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=1575&q=80"
            videoId={'z2hzmqllwf#'}
            width="100%"
          />
        ) : (
          <FeaturedCardsGrid />
        )
      }
    >
      <Flex
        minHeight="436px"
        width="100%"
        alignItems="center"
        justifyContent="center"
      >
        {isUpdating ? (
          <Transition isLoading={isUpdating} title="Updating your account" />
        ) : (
          <>
            {step.type === 'success' && (
              <FreeCardSuccess
                onSignUp={() => {
                  setStep({ type: 'create' })
                }}
                onNoThanks={actions.logout}
              />
            )}
            {step.type === 'create' && (
              <CreateAccount
                orderNumber={orderNumber}
                onCreatedAccount={() => {
                  setStep({ type: 'selectMySponsor' })
                }}
              />
            )}
            {step.type === 'selectMySponsor' && (
              <Suspense
                fallback={
                  <Flex
                    width="100%"
                    height="100%"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <LoadingSpinner size="large" />
                  </Flex>
                }
              >
                <SelectMySponsor
                  onSelectedSponsor={handleChooseSponsor}
                  onHasNoPossibleSponsors={handleHasNoPossibleSponsors}
                />
              </Suspense>
            )}
            {step.type === 'addSponsor' && (
              <AddSponsor
                onFindMySponsor={() => {
                  setStep({ type: 'findMySponsor' })
                }}
                onIDontKnowAnyone={handleChooseSponsor}
              />
            )}
            {step.type === 'findMySponsor' && (
              <FindMySponsor onSelectedSponsor={handleChooseSponsor} />
            )}
            {step.type === 'thankYou' &&
              (orderNumber ? (
                <ThankYou
                  chosenSponsor={chosenSponsor}
                  onClose={() => actions.openCatalog()}
                />
              ) : (
                <ThankYou
                  chosenSponsor={chosenSponsor}
                  onClose={
                    onClose ? () => onClose() : () => actions.openCatalog
                  }
                  shouldFinishAccountSetup={true}
                />
              ))}
          </>
        )}
      </Flex>
    </Sheet>
  )
}

export default SendFreeCardSuccess
